import { NgFor, NgIf } from '@angular/common';
import {
	Component,
	EventEmitter,
	Input,
	OnChanges,
	OnDestroy,
	Output
} from '@angular/core';
import { ChartConfiguration } from 'chart.js';
import { NgChartsModule } from 'ng2-charts';
import { Subject } from 'rxjs';
import { forkJoin } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { IconComponent, TypographyComponent } from 'components';
import {
	GetSocialListeningMyBrandAnalyticsWebsiteTrafficDemographicsService,
	WebsiteTrafficDemographics
} from '@brand-lolly/app/social-media-influencer/modules/social-listening/services/get-social-listening-my-brand-analytics-website-traffic-demographics.service';
import {
	DemographicsDataPoint,
	GetSocialListeningMyBrandAnalyticsDemographicsOverTimeService
} from '@brand-lolly/app/social-media-influencer/modules/social-listening/services/get-social-listening-my-brand-analytics-demographics-over-time.service';
import {
	ChartWidget,
	PreviewSlide,
	ReportDateRange,
	SummaryTextWidget,
	TitleWidget
} from '../../../../models/preview/preview.models';
import { ReportContentItem } from '../../../../services/report-settings.service';
import {
	applyContentItemOverrides,
	hasPositiveTotal
} from '../../../../services/report-preview-utils';
import { SlideNoDataComponent } from '../slide-no-data/slide-no-data.component';
import { ReportInlineTextComponent } from '../../components/report-inline-text/report-inline-text.component';
import {
	NO_SLIDE_TEXT_FIELDS,
	ReportSlideTextFields,
	slideTextFields
} from '../../../../services/report-inline-edit.service';
import { ReportSlideBrandComponent } from '../../components/report-slide-brand/report-slide-brand.component';

const MALE_COLOR = '#2563EB';
const FEMALE_COLOR = '#F4B8BC';

// Age-group palette, mirrored 1:1 from the LOLLY Figma frame (13302:101474).
// Order matches the "Audience profile" table + donut segments.
const AGE_GROUPS: readonly { id: string; label: string; color: string }[] = [
	{ id: 'age_18_24', label: '18–24', color: '#a98cf0' },
	{ id: 'age_25_34', label: '25–34', color: '#2872e1' },
	{ id: 'age_35_44', label: '35–44', color: '#6cd6c8' },
	{ id: 'age_45_54', label: '45–54', color: '#f5c451' },
	{ id: 'age_55_64', label: '55–64', color: '#f0a3c6' },
	{ id: 'age_65_plus', label: '65+', color: '#9aa3bb' }
];

const PLACEHOLDER = '—';

export interface AgeProfileRow {
	id: string;
	label: string;
	color: string;
	sessions: string;
	avgTime: string;
	bounce: string;
}

@Component({
	standalone: true,
	selector: 'app-website-traffic-demographics-slide',
	templateUrl: './website-traffic-demographics-slide.component.html',
	styleUrls: ['./website-traffic-demographics-slide.component.scss'],
	imports: [
		NgFor,
		NgIf,
		TypographyComponent,
		IconComponent,
		NgChartsModule,
		SlideNoDataComponent,
		ReportInlineTextComponent,
		ReportSlideBrandComponent
	]
})
export class WebsiteTrafficDemographicsSlideComponent
	implements OnChanges, OnDestroy
{
	@Input() item!: ReportContentItem;
	@Input() brandId!: string;
	@Input() dateRange!: ReportDateRange;
	@Input() themeColor: string | null = null;
	@Input() editorMode = false;
	@Output() slideBuilt = new EventEmitter<PreviewSlide>();

	/** The heading and summary the reader can type into, in the editor. */
	textFields: ReportSlideTextFields = NO_SLIDE_TEXT_FIELDS;

	title = '';
	titleColor = '#2872e1';
	dateLabel = '';
	summaryText = '';

	// Top-right KPI callout — Figma shows "25–34 share". No backing field exists
	// (the service only returns male/female split), so the value is a placeholder.
	kpiValue = PLACEHOLDER;
	readonly kpiLabel = '25–34 share';

	// "Audience profile" table rows — age-group labels are structural; every
	// metric ("Sessions" / "Avg. time" / "Bounce") is a placeholder because no
	// age-level analytics endpoint exists.
	ageRows: AgeProfileRow[] = AGE_GROUPS.map(group => ({
		id: group.id,
		label: group.label,
		color: group.color,
		sessions: PLACEHOLDER,
		avgTime: PLACEHOLDER,
		bounce: PLACEHOLDER
	}));

	// Neutral empty-state ring — no per-age session data to proportion the donut,
	// so we render a single muted segment rather than fabricate slice sizes.
	donutChartData: ChartConfiguration<'doughnut'>['data'] = {
		labels: ['No data'],
		datasets: [
			{
				data: [1],
				backgroundColor: ['#e6eaf2'],
				borderWidth: 0
			}
		]
	};
	readonly donutChartOptions: ChartConfiguration<'doughnut'>['options'] = {
		responsive: true,
		maintainAspectRatio: false,
		cutout: '62%',
		plugins: {
			legend: { display: false },
			tooltip: { enabled: false }
		}
	};
	readonly donutChartType = 'doughnut' as const;

	private readonly destroy$ = new Subject<void>();
	private cachedData: {
		timeline: DemographicsDataPoint[];
		traffic: WebsiteTrafficDemographics;
	} | null = null;
	private cacheKey: string | null = null;

	get isEmpty(): boolean {
		return (
			!this.cachedData ||
			!hasPositiveTotal([
				this.cachedData.traffic.male,
				this.cachedData.traffic.female,
				...this.cachedData.timeline.flatMap(point => [
					point.male,
					point.female
				])
			])
		);
	}

	constructor(
		private readonly demographicsService: GetSocialListeningMyBrandAnalyticsDemographicsOverTimeService,
		private readonly trafficService: GetSocialListeningMyBrandAnalyticsWebsiteTrafficDemographicsService
	) {}

	trackAgeRow(_: number, row: AgeProfileRow): string {
		return row.id;
	}

	ngOnChanges(): void {
		this.destroy$.next();
		if (!this.item || !this.brandId || !this.dateRange) return;

		const currentCacheKey = `${this.brandId}:${this.dateRange.start}:${this.dateRange.end}`;
		if (this.cachedData && this.cacheKey === currentCacheKey) {
			this.applyData(this.cachedData);
			return;
		}

		forkJoin({
			// Both must carry the report window, or the slide renders all-time
			// demographics under the report's date label.
			timeline: this.demographicsService.fetch(
				this.brandId,
				new Date(this.dateRange.start),
				undefined,
				new Date(this.dateRange.end)
			),
			traffic: this.trafficService.fetch(
				this.brandId,
				new Date(this.dateRange.start),
				undefined,
				new Date(this.dateRange.end)
			)
		})
			.pipe(takeUntil(this.destroy$))
			.subscribe(data => {
				this.cachedData = data;
				this.cacheKey = currentCacheKey;
				this.applyData(data);
			});
	}

	private applyData(data: {
		timeline: DemographicsDataPoint[];
		traffic: WebsiteTrafficDemographics;
	}): void {
		const rawSlide = this.buildSlide(data.traffic);
		const slide = applyContentItemOverrides(
			rawSlide,
			this.item,
			this.themeColor
		);
		this.updateViewModel(slide);
		this.slideBuilt.emit(slide);
	}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	private buildSlide(data: WebsiteTrafficDemographics): PreviewSlide {
		const prefix = `${this.item.id}_${this.brandId}`;

		const genderChartWidget: ChartWidget = {
			id: `${prefix}_gender_chart`,
			kind: 'chart',
			visible: true,
			content: {
				chartType: 'doughnut',
				dataSourceKey: 'gender_distribution',
				seriesMeta: [
					{ key: 'male', label: 'Male', color: MALE_COLOR },
					{ key: 'female', label: 'Female', color: FEMALE_COLOR }
				],
				chartJsConfig: {
					type: 'doughnut',
					data: {
						labels: ['Male', 'Female'],
						datasets: [
							{
								data: [data.male, data.female],
								backgroundColor: [MALE_COLOR, FEMALE_COLOR],
								borderWidth: 0
							}
						]
					}
				}
			}
		};

		return {
			id: `${prefix}_slide`,
			slideType: 'website_traffic_demographics',
			title: this.item.title || 'Website Traffic Demographics',
			description: this.item.description || '',
			dateRange: this.dateRange,
			widgets: [
				{
					id: `${prefix}_title`,
					kind: 'title',
					visible: true,
					content: {
						text: this.item.title || 'Website Traffic Demographics',
						color: '#2872e1'
					}
				},
				{
					id: `${prefix}_date`,
					kind: 'date_range',
					visible: true,
					content: { text: this.dateRange.label }
				},
				{
					id: `${prefix}_summary`,
					kind: 'summary_text',
					visible: true,
					content: { text: this.item.description || '' }
				},
				genderChartWidget
			]
		};
	}

	private updateViewModel(slide: PreviewSlide): void {
		this.textFields = slideTextFields(slide);
		const hidden = new Set(slide.overrides?.hiddenWidgetIds ?? []);

		const titleWidget = slide.widgets.find(
			widget => widget.kind === 'title' && !hidden.has(widget.id)
		) as TitleWidget | undefined;
		const dateWidget = slide.widgets.find(
			widget => widget.kind === 'date_range' && !hidden.has(widget.id)
		) as TitleWidget | undefined;
		const summaryWidget = slide.widgets.find(
			widget => widget.kind === 'summary_text' && !hidden.has(widget.id)
		) as SummaryTextWidget | undefined;

		this.title = titleWidget?.content.text ?? slide.title;
		this.titleColor = titleWidget?.content.color ?? '#2872e1';
		this.dateLabel =
			dateWidget?.content.text ?? slide.dateRange?.label ?? '';
		this.summaryText = summaryWidget?.content.text ?? '';
	}
}
